import { CENTERCOMPONENTS } from './CenterComponents';
import { ACTIVETAB } from './RightSideComponents';
import { useMapPanelContext } from './MapPanelContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAISMapContext } from '../AISMapContext';
import { useSelectionState } from '../../../components/SelectionStateProvider';
import { usePermissions } from '../../Permissions/PermissionsProvider';
import { useViewMode, ViewMode } from '../../../components/ViewModeProvider';

interface GridStyle {
  display: string;
  gridTemplateColumns: string;
  gridTemplateRows: string;
}

export interface GridClasses {
  layerControllerGrid: string;
  metadataGrid: string;
  reportLegendGrid: string;
  reportTableGrid: string;
  targetInfoGrid: string;
  mapPanelGrid: GridStyle;
  replayGrid: string;
  portInfoGrid: string;
}

const useGridClassCalculations = (
  tableExpanded: boolean,
  isSidebarActive: boolean,
  isReplayActive: boolean,
  isTargetPanelActive: boolean,
  isPortPanelActive: boolean,
  multipleTargets: boolean,
  multiplePorts: boolean,
  isTargetCardExpanded: boolean,
  isDrawingMode: boolean // Add Drawing mode flag
) => {
  const { tableHeight } = useMapPanelContext();

  // In Drawing mode, treat all panels as inactive
  const effectiveSidebarActive = isDrawingMode ? false : isSidebarActive;
  const effectiveReplayActive = isDrawingMode ? false : isReplayActive;
  const effectiveTargetPanelActive = isDrawingMode ? false : isTargetPanelActive;
  const effectivePortPanelActive = isDrawingMode ? false : isPortPanelActive;
  const effectiveTableExpanded = isDrawingMode ? false : tableExpanded;

  const getLayerControllerGridClass = useCallback(() => {
    let rowStart = 3;
    let rowEnd = 4;

    if (effectiveTableExpanded) {
      rowStart = rowStart - 1;
      rowEnd = rowEnd - 1;
    }

    return `col-start-1 col-end-2 row-start-${rowStart} row-end-${rowEnd}`;
  }, [effectiveTableExpanded]);

  const getMetadataGridClass = useCallback(() => {
    let colEnd = 7;
    let rowStart = 3;
    let rowEnd = 4;

    if (effectiveTableExpanded || effectiveReplayActive) {
      rowStart = rowStart - 1;
      rowEnd = rowEnd - 1;
    }

    if (effectiveReplayActive && effectiveTableExpanded) {
      colEnd = colEnd - 1;
    }

    if (effectiveSidebarActive) {
      colEnd = colEnd - 1;
    }

    if (effectiveTargetPanelActive) {
      // If target card is collapsed, shift metadata one column to the right
      if (!isTargetCardExpanded) {
        colEnd = colEnd;
      } else {
        colEnd = colEnd - 1;
      }
    }

    if (effectivePortPanelActive) {
      colEnd = colEnd - 1;
    }

    return `col-span-1 col-end-${colEnd} row-start-${rowStart} row-end-${rowEnd}`;
  }, [
    effectivePortPanelActive,
    effectiveReplayActive,
    effectiveSidebarActive,
    effectiveTargetPanelActive,
    isTargetCardExpanded,
    effectiveTableExpanded,
  ]);

  const getReportLegendGridClass = useCallback(() => {
    let colEnd = 7;
    let colSpan = 4;

    if (effectiveTargetPanelActive) {
      colEnd = colEnd - 1;
      colSpan = colSpan - 1;
    }

    if (effectivePortPanelActive) {
      colEnd = colEnd - 1;
      colSpan = colSpan - 1;
    }

    if (effectiveSidebarActive) {
      colEnd = colEnd - 1;
      colSpan = colSpan - 1;
    }

    return `col-span-${colSpan} col-end-${colEnd} row-start-1 row-end-2`;
  }, [effectivePortPanelActive, effectiveSidebarActive, effectiveTargetPanelActive]);

  const getReportTableGridClass = useCallback(() => {
    let colEnd = 7;

    if (effectiveSidebarActive) {
      colEnd = colEnd - 1;
    }

    if (effectiveTargetPanelActive) {
      // If target card is collapsed, don't subtract a column so table can expand
      if (isTargetCardExpanded) {
        colEnd = colEnd - 1;
      }
    }

    if (effectivePortPanelActive) {
      colEnd = colEnd - 1;
    }

    return `col-start-1 col-end-${colEnd} row-start-2 row-end-4`;
  }, [effectivePortPanelActive, effectiveSidebarActive, effectiveTargetPanelActive, isTargetCardExpanded]);

  const getTargetInfoGridClass = useCallback(() => {
    let colStart = 6;
    let colEnd = 7;
    let rowStart = 1;
    let rowEnd = 4;

    if (effectiveSidebarActive) {
      colStart = colStart - 1;
      colEnd = colEnd - 1;
    }

    // If target card is collapsed, only take up one row
    if (!isTargetCardExpanded) {
      rowStart = 1;
      rowEnd = 2;
    }

    return `col-start-${colStart} col-end-${colEnd} row-start-${rowStart} row-end-${rowEnd}`;
  }, [effectiveSidebarActive, isTargetCardExpanded]);

  const getPortInfoGridClass = useCallback(() => {
    let colStart = 6;
    let colEnd = 7;

    if (effectiveSidebarActive) {
      colStart = colStart - 1;
      colEnd = colEnd - 1;
    }

    if (effectiveTargetPanelActive) {
      colStart = colStart - 1;
      colEnd = colEnd - 1;
    }

    return `col-start-${colStart} col-end-${colEnd} row-start-1 row-end-4`;
  }, [effectiveSidebarActive, effectiveTargetPanelActive]);

  const getReplayInfoGridClass = useCallback(() => {
    let colStart = 6;
    let colEnd = 7;
    let rowStart = 3;
    let rowEnd = 4;

    if (effectiveTargetPanelActive) {
      if (isTargetCardExpanded) {
        colStart = colStart - 1;
        colEnd = colEnd - 1;
      }
    }

    if (effectivePortPanelActive) {
      colStart = colStart - 1;
      colEnd = colEnd - 1;
    }

    if (effectiveTableExpanded) {
      rowStart = rowStart - 1;
      rowEnd = rowEnd - 1;
    }

    return `col-start-${colStart} col-end-${colEnd} row-start-${rowStart} row-end-${rowEnd}`;
  }, [
    effectivePortPanelActive,
    effectiveTargetPanelActive,
    effectiveTableExpanded,
    isTargetCardExpanded,
  ]);

  const getMapPanelGridStyles = useCallback(() => {
    // Calculate the same values as before
    let fourthCol = 'auto';
    let fifthCol = 'auto';
    let sixthCol = 'auto';

    if (effectiveSidebarActive) {
      sixthCol = '384px';
      if (effectiveTargetPanelActive) {
        fifthCol = multipleTargets ? '488px' : '464px';
        if (effectivePortPanelActive) {
          fourthCol = multiplePorts ? '488px' : '464px';
        }
      } else if (effectivePortPanelActive) {
        fifthCol = multiplePorts ? '488px' : '464px';
      }
    } else if (effectiveTargetPanelActive) {
      sixthCol = multipleTargets ? '488px' : '464px';
      if (effectivePortPanelActive) {
        fifthCol = multiplePorts ? '488px' : '464px';
        if (effectiveReplayActive) {
          fourthCol = '560px';
        }
      } else if (effectiveReplayActive) {
        fifthCol = '560px';
      }
    } else if (effectivePortPanelActive) {
      sixthCol = multiplePorts ? '488px' : '464px';
      if (effectiveReplayActive) {
        fifthCol = '560px';
      }
    } else if (effectiveReplayActive) {
      sixthCol = '560px';
    }

    return {
      display: 'grid',
      gridTemplateColumns: `auto 1fr auto ${fourthCol} ${fifthCol} ${sixthCol}`,
      gridTemplateRows: `auto 1fr ${
        effectiveTableExpanded ? `${tableHeight}px` : 'auto'
      }`,
    };
  }, [
    effectivePortPanelActive,
    effectiveReplayActive,
    effectiveSidebarActive,
    effectiveTargetPanelActive,
    multiplePorts,
    multipleTargets,
    effectiveTableExpanded,
    tableHeight,
  ]);
  return {
    getLayerControllerGridClass,
    getMetadataGridClass,
    getReportLegendGridClass,
    getReportTableGridClass,
    getTargetInfoGridClass,
    getPortInfoGridClass,
    getReplayInfoGridClass,
    getMapPanelGridStyles,
  };
};

export const useGridClasses = (isTargetCardExpanded: boolean): GridClasses => {
  const { activeTab, centerComponent, tableExpanded, reportDetailsExpanded } =
    useMapPanelContext();
  const { targetInfoDetailsList, selectedPorts, portSelectionMode } = useSelectionState();
  const permissions = usePermissions();
  const { viewMode } = useViewMode();

  // Check if we're in Drawing or Kiosk mode (minimal UI)
  const isDrawingMode = viewMode === ViewMode.Drawing || viewMode === ViewMode.Kiosk;

  const isTableExpandedAndActive = useMemo(() => {
    if (!tableExpanded) {
      return false;
    }

    if (centerComponent === CENTERCOMPONENTS.NONE) {
      return false;
    }

    return true;
  }, [tableExpanded, centerComponent]);

  const isReplayActive = useMemo(() => {
    return activeTab === ACTIVETAB.REPLAY;
  }, [activeTab]);

  const isSidebarActive = useMemo(() => {
    if (activeTab === ACTIVETAB.NONE) {
      return false;
    }

    if (activeTab === ACTIVETAB.REPLAY) {
      return false;
    }

    // This is where the report sidebar is active, but hidden
    if (!reportDetailsExpanded) {
      return false;
    }

    return true;
  }, [activeTab, reportDetailsExpanded]);

  const isTargetPanelActive = useMemo(() => {
    if (targetInfoDetailsList.length === 0) {
      return false;
    }

    return true;
  }, [targetInfoDetailsList]);

  const isPortPanelActive = useMemo(() => {
    if (selectedPorts.length === 0) {
      return false;
    }

    // Port panel is only active for 'nextport' and 'popout' modes
    // For 'routes' mode, only the routes table is shown (no side panel)
    if (portSelectionMode === 'routes') {
      return false;
    }

    return true;
  }, [selectedPorts, portSelectionMode]);

  const {
    getLayerControllerGridClass,
    getMetadataGridClass,
    getReportLegendGridClass,
    getReportTableGridClass,
    getMapPanelGridStyles,
    getReplayInfoGridClass,
    getTargetInfoGridClass,
    getPortInfoGridClass,
  } = useGridClassCalculations(
    isTableExpandedAndActive,
    isSidebarActive,
    isReplayActive,
    isTargetPanelActive,
    isPortPanelActive,
    targetInfoDetailsList.length > 1,
    selectedPorts.length > 1,
    isTargetCardExpanded,
    isDrawingMode // Pass Drawing mode flag
  );

  const [layerControllerGrid, setLayerControllerGrid] = useState<string>(
    getLayerControllerGridClass()
  );

  const [metadataGrid, setMetadataGrid] = useState<string>(
    getMetadataGridClass()
  );

  const [reportLegendGrid, setReportLegendGrid] = useState<string>(
    getReportLegendGridClass()
  );

  const [reportTableGrid, setReportTableGrid] = useState<string>(
    getReportTableGridClass()
  );

  const [portInfoGrid, setPortInfoGrid] = useState<string>(
    getPortInfoGridClass()
  );

  const [targetInfoGrid, setTargetInfoGrid] = useState<string>(
    getTargetInfoGridClass()
  );

  const [replayGrid, setReplayGrid] = useState<string>(
    getReplayInfoGridClass()
  );

  const [mapPanelGrid, setMapPanelGrid] = useState<GridStyle>(
    getMapPanelGridStyles()
  );

  useEffect(() => {
    setMapPanelGrid((prevMapPanelGrid) => {
      const newMapPanelGrid = getMapPanelGridStyles();
      return prevMapPanelGrid !== newMapPanelGrid
        ? newMapPanelGrid
        : prevMapPanelGrid;
    });

    setLayerControllerGrid((prevLayerControllerGrid) => {
      const newLayerControllerGrid = getLayerControllerGridClass();
      return prevLayerControllerGrid !== newLayerControllerGrid
        ? newLayerControllerGrid
        : prevLayerControllerGrid;
    });

    setMetadataGrid((prevMetadataGrid) => {
      const newMetadataGrid = getMetadataGridClass();
      return prevMetadataGrid !== newMetadataGrid
        ? newMetadataGrid
        : prevMetadataGrid;
    });

    setReportLegendGrid((prevReportLegendGrid) => {
      const newReportLegendGrid = getReportLegendGridClass();
      return prevReportLegendGrid !== newReportLegendGrid
        ? newReportLegendGrid
        : prevReportLegendGrid;
    });

    setReportTableGrid((prevReportTableGrid) => {
      const newReportTableGrid = getReportTableGridClass();
      return prevReportTableGrid !== newReportTableGrid
        ? newReportTableGrid
        : prevReportTableGrid;
    });

    setPortInfoGrid((prevPortInfoGrid) => {
      const newPortInfoGrid = getPortInfoGridClass();
      return prevPortInfoGrid !== newPortInfoGrid
        ? newPortInfoGrid
        : prevPortInfoGrid;
    });

    setTargetInfoGrid((prevTargetInfoGrid) => {
      const newTargetInfoGrid = getTargetInfoGridClass();
      return prevTargetInfoGrid !== newTargetInfoGrid
        ? newTargetInfoGrid
        : prevTargetInfoGrid;
    });

    setReplayGrid((prevReplayGrid) => {
      const newReplayGrid = getReplayInfoGridClass();
      return prevReplayGrid !== newReplayGrid ? newReplayGrid : prevReplayGrid;
    });
  }, [
    getLayerControllerGridClass,
    getMapPanelGridStyles,
    getMetadataGridClass,
    getPortInfoGridClass,
    getReplayInfoGridClass,
    getReportLegendGridClass,
    getReportTableGridClass,
    getTargetInfoGridClass,
  ]);

  return {
    layerControllerGrid,
    metadataGrid,
    reportLegendGrid,
    reportTableGrid,
    targetInfoGrid,
    mapPanelGrid,
    replayGrid,
    portInfoGrid,
  };
};
